(ns ncr.fragment.props.money.input
  (:require ["bignumber.js" :as big]
            [cljs.core.async :as async :refer-macros [go] :refer [<!]]
            [clojure.string :as s]
            [goog.dom :as gdom]
            [goog.dom.TagName :as TagName]
            [goog.events.KeyCodes :as KeyCodes]
            [goog.math :as gm]
            [goog.style :as gs]
            [lambdaisland.glogi :as log]
            [ncr.etc.alert :as a]
            [ncr.backend :as b]
            [ncr.etc.pf :as pf]
            [ncr.widgets.core :as w]
            [ncr.widgets.pf :as wpf]
            [ncr.etc.util :as u]
            [ncr.fragment.common :as fc]
            [ncr.fragment.props.money :as money]
            [uix.core.alpha :as uix]))

(def ^:private vat-suggestions (map big/BigNumber. [0 0.07 0.19 0.21]))

(defn- numbers-not-equal? [^js a b]
  (if a (not (.isEqualTo a b)) b))

(defn- c-validated-input [{:keys [field-id ^js value on-change on-submit on-cancel on-validate on-focus on-mount]}]
  (let [^js original-value value
        *value (uix/state (money/to-string value))
        value @*value
        parsed (uix/memo #(when (and value (not (s/blank? value))) (money/parse value)) [value])
        valid? (uix/memo #(money/valid? parsed) [parsed])

        *focused? (uix/state false)
        focused? @*focused?
        on-focus (uix/callback (fn [] (some-> on-focus (apply [])) (reset! *focused? true)) [on-focus])

        *on-mount-pending? (uix/state true)
        on-mount-pending? @*on-mount-pending?
        set-ref! (uix/callback
                   (fn [el]
                     (when (and el on-mount on-mount-pending?)
                       (reset! *on-mount-pending? false)
                       (on-mount el)))
                   [on-mount-pending? on-mount])]

    (uix/effect! #(on-validate valid?) [valid?])
    (uix/effect!
      #(when valid?
         (if focused?
           (when (numbers-not-equal? original-value parsed) (on-change parsed))
           (when (not= (money/to-string original-value) value) (reset! *value (money/to-string original-value)))))
      [parsed original-value valid? focused? on-change])

    [pf/c-text-input
     {:value value
      :auto-complete :off
      :id field-id
      :validated (when-not (or valid? (s/blank? value)) :error)
      :on-change #(reset! *value %)
      :on-focus on-focus
      :on-blur #(reset! *focused? false)
      :ref set-ref!
      :on-key-up
      (fn [event]
        (condp = (.-keyCode event)

          KeyCodes/ESC
          (do
            (if (.isEqualTo original-value parsed)
              (on-cancel)
              (reset! *value (money/to-string original-value)))
            (.preventDefault event))

          KeyCodes/ENTER
          (do
            (when valid? (on-submit parsed))
            (.preventDefault event))

          nil))}]))

(defn- c-inline-money-input [{:keys [id] :as args}]
  (let [field-id (uix/memo #(gensym (str "inline-netto-brutto--" (name id) "-")) [])]
    [pf/c-form-group {:label (s/capitalize (name id)) :field-id field-id}
     [c-validated-input (assoc args :field-id field-id)]]))

(defn- vat->str [^js vat]
  (some-> vat (.times 100) (.decimalPlaces 2) .toString (str "%")))

(defn- value->percent [^js value]
  (some-> value (.div 100)))

(defn- c-vat-input [{:keys [^js value on-focus on-change on-submit on-validate on-cancel]}]
  (let [suggestions vat-suggestions
        field-id (uix/memo #(gensym (str "inline-netto-brutto--vat-")) [])
        value-str (uix/memo #(some-> value (.times 100)) [value])]

    [pf/c-form-group
     {:label "VAT"
      :class :vat
      :field-id field-id
      :helper-text
      [pf/c-form-helper-text {:component :div :is-hidden false}
       [pf/c-chip-group {:num-chips (count suggestions)}
        (for [v suggestions]
          ^{:key v}
          [wpf/c-chip (vat->str v)
           (fn [^js e]
             (if (= 1 (.-detail e))
               (on-change v)
               (on-submit v)))])]]}
     [pf/c-input-group
      [c-validated-input
       {:field-id field-id
        :value value-str
        :on-focus on-focus
        :on-change (comp on-change value->percent)
        :on-submit (comp on-submit value->percent)
        :on-cancel on-cancel
        :on-validate on-validate}]
      [pf/c-input-group-text
       [w/c-mdi-icon {:icon :percent}]]]]))

(defn- c-inline-vat-switch [{:keys [enabled? direction on-toggle]}]
  [pf/c-button
   (merge {:class [:vat-switch (when-not on-toggle :disabled)] :variant :plain}
     (when on-toggle
       {:on-click on-toggle}))
   [w/c-mdi-icon
    {:class [:mdi-18px (if enabled? :vat-enabled :vat-disabled)]
     :icon (if (= direction :left) :arrow-left-thick :arrow-right-thick)}]])

(defn- update-primary [{:keys [primary] :as state} k]
  (let [p (if (get state k)
            k (let [nk (if (= k :netto) :brutto :netto)]
                (when (get state nk) nk)))]
    (if (and p (not= p primary))
      (assoc state :primary p)
      state)))

(defn calculate-inverse
  "keep vat, update other value when possible"
  [^js vat k ^js v]
  (when (and vat v)
    (if (= :netto k)
      {:brutto (-> v (.times vat) (.plus v) money/to-bignumber)}
      {:netto (-> v (.div (.plus vat 1)) money/to-bignumber)})))

(defn- sanitize-vat [^js vat]
  (when (and (.isFinite vat) (not (or (.isNaN vat) (.isZero vat) (.isLessThanOrEqualTo vat -1))))
    vat))

(defn calculate-vat
  "keep counterpart value, calculate VAT. `nil` means VAT is invalid and should be reset"
  [k ^js v ^js inverse]
  (when (and v inverse)
    (if (.isEqualTo v inverse)
      (money/to-bignumber 0)
      (let [[netto ^js brutto] (if (= k :netto) [v inverse] [inverse v])]
        (-> brutto (.div netto) (.minus 1) sanitize-vat)))))

(defn calculate-values [values k ^js v]
  (let [{:keys [netto brutto primary] :as result} (assoc values k v)]
    (merge result
      (if (= :vat k)
        (when-let [[pk pv]
                   (and v (or
                            (when-let [pv (get result primary)] [primary pv])
                            (when (and (nil? netto) brutto) [:brutto brutto])
                            (when netto [:netto netto])))]
          (merge {:primary pk} (calculate-inverse v pk pv)))
        (merge
          {:primary k}
          (when v
            (or
              (when (or primary (nil? (if (= :netto k) brutto netto))) (calculate-inverse (:vat result) k v))
              (when-let [nv (if (= :netto k) brutto netto)]
                {:vat (calculate-vat k v nv)}))))))))

(def ^:private container-margin -10)

(defn- center-popover [popover anchor get-left-top]
  (let [^js b (gs/getBounds anchor)
        ^js pb (gs/getBounds popover)
        [left top] (get-left-top b pb)
        ^js r (u/fit-rect
                (gm/Rect. left top (.-width pb) (.-height pb))
                (.createFromBox ^js gm/Rect
                  (.expand (gs/getVisibleRectForElement popover)
                    container-margin container-margin container-margin container-margin)))]
    (gs/setPosition popover (.-left r) (.-top r))))

(defn- position-at [target ^js anchor]
  (let [[top-margin left-margin] [-8 -9]
        ^js popover (gdom/getAncestorByTagNameAndClass target TagName/DIV "pf-c-popover")
        ^js br (gs/getRelativePosition popover target)]
    (center-popover popover (or (.querySelector anchor "span.value") anchor)
      (fn [b _]
        [(+ (.-left b) (.-x br) left-margin) (+ (.-top b) (.-y br) top-margin)]))))

(defn- center-at [target anchor]
  (center-popover
    (gdom/getAncestorByTagNameAndClass target TagName/DIV "pf-c-popover")
    (gdom/getAncestorByTagNameAndClass anchor TagName/DIV "pf-c-input-group")
    (fn [b pb]
      [(- (.-left b) (/ (- (.-width pb) (.-width b)) 2)) (- (.-top b) (/ (- (.-height pb) (.-height b)) 2))])))

(defn- c-inline-editor [{:keys [values on-change anchor anchor-ref on-cancel position]}]
  (let [*state (uix/state
                 (let [{:keys [^js vat]} values]
                   (assoc values
                     :linked? (and vat (.isEqualTo vat (sanitize-vat vat)))
                     :primary anchor)))
        {:keys [^js netto brutto vat linked? primary] :as state} @*state
        *target-ref (uix/state nil)
        *valid? (uix/state {})

        calculate! (uix/callback
                     (fn [values k v]
                       (let [linked? (or (= :vat k) linked?)]
                         (merge
                           {:linked? linked?}
                           (calculate-values (if linked? values (dissoc values :primary)) k v))))
                     [linked?])]

    (uix/effect! #(when-let [t @*target-ref]
                    ((if (= position :center) center-at position-at) t @anchor-ref))
      [@*target-ref])

    [:div.ncr-m-inline-edit--netto-brutto
     [:div.ncr-m-inline-edit--netto-brutto--values
      (for [k [:netto :vat-enabled? :brutto]]
        (if (= :vat-enabled? k)
          [c-inline-vat-switch
           {:key k
            :enabled? linked?
            :direction (if (= :netto primary) :right :left)
            :on-toggle (when (or vat (and netto brutto))
                         #(swap! *state update :linked? not))}]

          (let [^js value (get state k)]
            [c-inline-money-input
             {:key k
              :id k
              :value value
              :on-focus (partial swap! *state update-primary k)
              :on-change (partial swap! *state calculate! k)
              :on-submit (comp on-change (partial calculate! state k))
              :on-cancel on-cancel
              :on-validate (partial swap! *valid? assoc k)
              :on-mount (if (= anchor k)
                          (fn [^js el]
                            (reset! *target-ref el)
                            (.select el)
                            (.focus el))
                          identity)}])))]
     [pf/c-input-group-text {:variant :plain :class :ncr-m-inline-edit--netto-brutto--currency}
      [w/c-mdi-icon {:icon :currency-eur}]]

     (let [k :vat]
       [c-vat-input
        {:key k
         :value vat
         :on-validate (partial swap! *valid? assoc k)
         :on-cancel on-cancel
         :on-change (partial swap! *state calculate! k)
         :on-submit (comp on-change (partial calculate! state k))}])
     [:<>
      [pf/c-button
       {:variant :link
        :class :button-save
        :is-disabled (not-every? true? (vals @*valid?))
        :icon [pf/c-check-icon]
        :on-click #(on-change state)}]
      [pf/c-button
       {:variant :plain
        :class :button-cancel
        :icon [pf/c-times-icon]
        :on-click on-cancel}]]]))

(defn- show-vat [{:keys [value include-parens?]}]
  (if include-parens?
    (if value [:<> "(" (vat->str value) ")"] [:<>])
    (if value (vat->str value) [:span.value-unknown "\u2014"])))

(defn- show-brutto [{:keys [display value ^js vat]}]
  (let [brutto-span (if value [:span (money/to-string value)] [:span.value-unknown "\u2014"])]
    (case display
      :absolute brutto-span
      :percentage [show-vat {:value vat :include-parens? false}]
      :both [:span brutto-span
             (when (vat->str vat)
               [:<> " " [show-vat {:value vat :include-parens? true}]])])))

(defn- show-netto [{:keys [value]}]
  (if value
    (money/to-string value)
    [:span.value-unknown "\u2014"]))


(defn c-netto-brutto-input-group [{:keys [values display-netto display-brutto ignore-missing? updating?
                                          refs on-item-click display-type hints on-apply-hint
                                          input-group-classes]}]
  (let [{:keys [^js netto ^js brutto ^js vat]} values
        [class variant] (if (= :label display-type) [:ncr-c-netto-brutto-label :link] [:ncr-c-netto-brutto-input-group :control])
        refs (or refs {:netto (uix/ref) :brutto (uix/ref)})
        display-values (uix/memo
                         #(concat
                            (when-not (or (= :none display-netto) (and ignore-missing? (not netto))) [:netto])
                            (when-not (or (= :none display-brutto) (and ignore-missing? (not brutto))) [:brutto]))
                         [display-netto display-brutto netto brutto ignore-missing?])]
    [:<>
     [pf/c-input-group {:class (into [:ncr-m-inline-edit--netto-brutto class] input-group-classes)}
      (if (seq display-values)
        (for [k display-values :let [^js ref (get refs k) ^js value (get values k)]]
          [pf/c-tooltip
           {:key k
            :content [:span "Edit " (name k) " value"] :position :top :enable-flip true}
           [pf/c-button
            {:variant variant
             :ref ref ; we need ref to position popup properly
             :is-disabled updating?
             :class [:pf-u-flex-grow-1 :ncr-m-inline-edit k]
             :on-click #(on-item-click k ref)}
            [:span.value
             {:class (when value (if (.isNegative value) :ncr-u-money-expense :ncr-u-money-income))}
             (if (= :brutto k)
               [show-brutto {:value value :display display-brutto :vat vat}]
               [show-netto {:value value}])]]])
        (let [k :netto
              ^js ref (get refs k)]
          [pf/c-button
           {:variant variant
            :ref ref
            :class [:pf-u-flex-grow-1 :ncr-m-inline-edit k]
            :on-click #(on-item-click k ref)}
           "No value"]))
      [pf/c-input-group-text {:variant :plain}
       (if updating?
         [pf/c-spinner {:class :ncr-m-visible :is-inline true}]
         [pf/c-button
          {:variant :link
           :class :ncr-m-inline-edit--add-on
           :on-click #(on-item-click :netto (:netto refs))}
          [:div.icon-container
           [w/c-mdi-icon {:icon :pencil}]
           [w/c-mdi-icon {:icon :currency-eur}]]])]]
     (when (= :control variant)
       [pf/c-chip-group
        (when (seq hints)
          (for [value hints]
            ^{:key value}
            [wpf/c-chip (money/to-string value)
             (fn [^js event]
               (let [f #(on-apply-hint % value)]
                 (u/on-click-modifiers event
                   {:on-shift-click (fn [_] (f :brutto))
                    :on-click (fn [_] (f :netto))})))]))])]))

(defn c-inline-editor-popover [{:keys [anchor anchor-ref on-change on-cancel on-status values position]
                                :or {position :center}}]
  (let [values (uix/memo
                 (fn []
                   (let [{:keys [netto brutto vat]} values]
                     (assoc values :vat (or vat (calculate-vat :netto netto brutto)))))
                 [values])
        update! (uix/callback
                  (fn [v]
                    (on-cancel)
                    (on-status :updating)
                    (go (on-status (when (= :error (<! (on-change v))) :error))))
                  [on-change])]

    [pf/c-popover
     {:is-visible (boolean anchor)
      :class-name "inline-edit"
      :reference anchor-ref
      :should-close on-cancel
      :has-auto-width true
      :hide-on-outside-click true
      :show-close false ; default `x` is duplicating "cancel" button
      :body-content
      [c-inline-editor
       {:anchor anchor
        :anchor-ref anchor-ref
        :on-cancel on-cancel
        :values values
        :position position
        :on-change update!}]}]))


(defn c-inline-input-netto-brutto
  [{:keys [on-change ^js netto display-netto ^js brutto display-brutto display-type ignore-missing? hints validated]}]
  (let [*status (uix/state nil)
        *popover (uix/state nil)
        values (uix/memo
                 #(hash-map
                    :netto netto
                    :brutto brutto
                    :vat (calculate-vat :netto netto brutto))
                 [netto brutto])
        update! (uix/callback
                  (fn [v]
                    (reset! *popover nil)
                    (reset! *status :updating)
                    (go (reset! *status (when (= :error (<! (on-change v))) :error))))
                  [on-change])
        refs {:netto (uix/ref)
              :brutto (uix/ref)}]
    [:<>
     [c-inline-editor-popover
      {:anchor (first @*popover)
       :anchor-ref (second @*popover)
       :on-change update!
       :on-cancel #(reset! *popover nil)
       :on-status #(reset! *status %)
       :values values}]

     [c-netto-brutto-input-group
      (merge
        {:values values
         :display-type display-type
         :display-netto display-netto
         :display-brutto display-brutto
         :ignore-missing? ignore-missing?
         :updating? (= :updating @*status)
         :refs refs
         :on-item-click #(reset! *popover %&)
         :input-group-classes [(when (and (some? validated) (not validated)) :ncr-m-error)]}
        (when (= :input-group display-type)
          {:hints hints
           :on-apply-hint (fn [t v] (update! (assoc values t v)))}))]]))

(defn- load-properties [fragment-id on-success]
  (go
    (u/when-fail
      [error
       (u/error-m
         [properties (<! (b/!query [:properties_load {:reference fragment-id}]))
          properties (u/transit-read properties)]
         (on-success properties))]
      (do
        (a/toast!
          {:variant :danger
           :title "Error"
           :body "Error loading fragment properties"})
        (log/error :error error)))))

(defn c-inline-input [{:keys [fragment-id ^js value on-updated]}]
  (let
    [*status (uix/state nil)
     status @*status
     ref (uix/ref)
     updating? (= :updating status)
     *anchor (uix/state nil)
     *values (uix/state nil)
     on-change (uix/callback
                 (fn [{:keys [netto brutto]}]
                   (reset! *status :updating)
                   (go
                     (reset! *status
                       (when (= :error (<! (fc/update-props {:common/netto netto :common/brutto brutto}
                                             "Netto/Brutto" fragment-id)))
                         :error))
                     (on-updated)))
                 [fragment-id on-updated])]

    [:<>

     [c-inline-editor-popover
      {:anchor @*anchor
       :anchor-ref ref
       :on-change on-change
       :on-cancel #(reset! *anchor nil)
       :on-status #(reset! *status %)
       :position :precise
       :values @*values}]

     [pf/c-button
      {:variant :link
       :is-disabled updating?
       :class :ncr-m-inline-edit
       :icon-position :right
       :ref ref
       :icon (if updating?
               [pf/c-spinner {:class :ncr-m-visible :is-inline true}]
               [w/c-mdi-icon {:icon :pencil}])
       :on-click (fn []
                   (reset! *status :updating)
                   (load-properties fragment-id
                     (fn [props]
                       (reset! *status nil)
                       (reset! *values {:netto (:common/netto props)
                                        :brutto (:common/brutto props)})
                       (reset! *anchor :netto))))}
      [:span
       {:class (if (.isNegative value)
                 :ncr-u-money-expense :ncr-u-money-income)}
       (money/to-string value)]]]))
